import { useLocalSearchParams, useRouter } from "expo-router";
import Head from "expo-router/head";
import { useAtom, useAtomValue } from "jotai";
import { DateTime } from "luxon";
import { useCallback, useEffect, useRef, useState } from "react";
import { ActivityIndicator, StyleSheet, View } from "react-native";

import type { DisplayEvent } from "@/lib/event-helpers";

import { eventFamily } from "@/atoms/event";
import { currentUserIdAtom } from "@/atoms/session";
import { BackgroundImageBlur } from "@/components/BackgroundImageBlur";
import { EventDisplay, type EventFormValues } from "@/components/event-display";
import { GText } from "@/components/g-text";
import { colors } from "@/constants/colors";
import { useEventMembers } from "@/hooks/use-event-members-sync";
import { usePayment } from "@/hooks/use-payment";
import Bugsnag from "@/lib/bugsnag";
import { toDisplayEvent } from "@/lib/event-helpers";
import {
  fetchEventRawBySlug,
  updateEvent,
  upsertTicketKind,
} from "@/lib/event-queries";
import { formatDescription } from "@/utils/formatStrings";

export default function EventScreen() {
  const { mode, slug } = useLocalSearchParams<{
    mode?: string;
    slug: string;
  }>();
  const router = useRouter();
  const isEditing = mode === "edit";
  const userId = useAtomValue(currentUserIdAtom);

  const [event, setEvent] = useAtom(eventFamily(slug));
  const rawRef = useRef<null | Record<string, unknown>>(null);
  const [ticketKindId, setTicketKindId] = useState<null | string>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<null | string>(null);
  const [flyerUrl, setFlyerUrl] = useState<null | string>(null);

  const eventId = event?.id ?? null;
  const { guests, hosts, myRsvp } = useEventMembers(eventId ?? "", userId);

  // Initial values for edit mode — derived from raw data on first load
  const [initialValues, setInitialValues] =
    useState<null | Partial<EventFormValues>>(null);

  useEffect(() => {
    if (!slug) return;
    setLoading(true);
    fetchEventRawBySlug(slug)
      .then((data) => {
        if (!data) {
          setError("event not found");
          return;
        }
        const raw = data as Record<string, unknown>;
        rawRef.current = raw;
        setEvent(toDisplayEvent(raw as Parameters<typeof toDisplayEvent>[0]));
        setInitialValues(rawToInitialValues(raw));
        const tier = (
          raw.event_ticket_kind as Record<string, unknown>[] | undefined
        )?.[0];
        setTicketKindId((tier?.id as string) ?? null);
        if (raw.flyer_url) setFlyerUrl(raw.flyer_url as string);
      })
      .catch((e: unknown) => {
        Bugsnag.notify(e instanceof Error ? e : new Error(String(e)));
        setError("Failed to load event");
      })
      .finally(() => setLoading(false));
  }, [slug, setEvent]);

  const handleFlyerUrlChange = useCallback((url: string) => {
    setFlyerUrl(url);
  }, []);

  const handleSave = useCallback(
    async (values: EventFormValues) => {
      if (!eventId) throw new Error("Missing event ID");

      const previousEvent = event;
      const raw = rawRef.current;

      const location_geo =
        values.location_lat && values.location_lng
          ? `POINT(${values.location_lng} ${values.location_lat})`
          : null;

      const fullEventData: Record<string, unknown> = {
        description: formatDescription(values.description),
        ends_at: (() => {
          const start = DateTime.fromISO(values.starts_at);
          if (!start.isValid) return null;
          const dur = parseInt(values.duration_minutes, 10) || 120;
          return start.plus({ minutes: dur }).toISO()!;
        })(),
        flyer_url: values.flyer_url || null,
        location_city: values.location_city,
        location_country: values.location_country,
        location_geo: location_geo,
        location_name: values.location_name,
        location_neighborhood: values.location_neighborhood || null,
        location_place_id: values.location_place_id || null,
        location_postal_code: values.location_postal_code,
        location_state: values.location_state,
        location_street: values.location_street,
        starts_at: values.starts_at || null,
        status: values.status,
        timezone: values.timezone,
        title: formatDescription(values.title),
        visibility: values.visibility,
      };

      // Optimistically update atom and raw ref
      const updatedRaw = raw ? { ...raw, ...fullEventData } : null;
      if (updatedRaw) {
        rawRef.current = updatedRaw;
        setEvent(
          toDisplayEvent(updatedRaw as Parameters<typeof toDisplayEvent>[0]),
        );
        setInitialValues(rawToInitialValues(updatedRaw));
      }

      try {
        const changedEventData: Record<string, unknown> = {};
        if (raw) {
          for (const [key, value] of Object.entries(fullEventData)) {
            if (JSON.stringify(value) !== JSON.stringify(raw[key])) {
              changedEventData[key] = value;
            }
          }
        }

        if (Object.keys(changedEventData).length > 0) {
          const ok = await updateEvent(eventId, changedEventData);
          if (!ok) throw new Error("Failed to update event");
        }

        const priceCents = parseInt(values.price_cents, 10) || 0;
        const originalTier = (
          raw?.event_ticket_kind as Record<string, unknown>[]
        )?.[0];
        const quantityTotal = parseInt(values.quantity_total, 10) || 10;
        const ticketData = {
          currency: values.currency || "usd",
          description: values.ticket_description || undefined,
          name: values.ticket_name || "General Admission",
          price_cents: priceCents,
          quantity_total: quantityTotal,
        };

        if (priceCents > 0 || values.ticket_name) {
          const ticketChanged =
            !originalTier ||
            ticketData.currency !== originalTier.currency ||
            ticketData.description !==
              (originalTier.description ?? undefined) ||
            ticketData.name !== originalTier.name ||
            ticketData.price_cents !== originalTier.price_cents ||
            ticketData.quantity_total !== originalTier.quantity_total;

          if (ticketChanged) {
            await upsertTicketKind(eventId, ticketData, ticketKindId);
          }
        }
      } catch (e: unknown) {
        // Revert optimistic update on failure
        if (previousEvent) {
          setEvent(previousEvent);
          if (raw) {
            rawRef.current = raw;
            setInitialValues(rawToInitialValues(raw));
          }
        }
        throw e;
      }
    },
    [eventId, event, ticketKindId, setEvent],
  );

  // Payment hook
  const paymentEvent = event ?? ({} as DisplayEvent);
  const {
    loading: paymentLoading,
    paymentUI,
    showSuccess,
    startPayment,
  } = usePayment(paymentEvent, myRsvp?.rsvp_status ?? null);

  if (loading) {
    return (
      <View style={styles.container}>
        <BackgroundImageBlur displayFlyerUrl={flyerUrl} />
        <View style={[styles.fill, styles.center]}>
          <ActivityIndicator color={colors.white} size="large" />
        </View>
      </View>
    );
  }

  if (error || !event || !initialValues) {
    return (
      <View style={styles.container}>
        <BackgroundImageBlur displayFlyerUrl={flyerUrl} />
        <View style={[styles.fill, styles.center]}>
          <GText style={styles.errorText}>whoops!</GText>
          <GText style={styles.errorText}>{error ?? "event not found"}</GText>
        </View>
      </View>
    );
  }

  const truncatedDescription = event.description
    ? event.description.slice(0, 160)
    : undefined;

  return (
    <View style={styles.container}>
      <BackgroundImageBlur displayFlyerUrl={flyerUrl} />

      {!isEditing && (
        <Head>
          {event.title ? <title>{event.title}</title> : null}
          {event.title ? (
            <meta content={event.title} property="og:title" />
          ) : null}
          {event.title ? (
            <meta content={event.title} name="twitter:title" />
          ) : null}
          {truncatedDescription ? (
            <meta content={truncatedDescription} name="description" />
          ) : null}
          {truncatedDescription ? (
            <meta content={truncatedDescription} property="og:description" />
          ) : null}
          {event.flyer_url ? (
            <meta content={event.flyer_url} property="og:image" />
          ) : null}
          {truncatedDescription ? (
            <meta content={truncatedDescription} name="twitter:description" />
          ) : null}
          {event.flyer_url ? (
            <meta content={event.flyer_url} name="twitter:image" />
          ) : null}
          <meta content="summary_large_image" name="twitter:card" />
        </Head>
      )}

      <EventDisplay
        event={event}
        guests={guests}
        hosts={hosts}
        initialValues={initialValues}
        mode={isEditing ? "edit" : "view"}
        myRsvp={myRsvp}
        onFlyerUrlChange={handleFlyerUrlChange}
        onGetTicket={startPayment}
        onSave={handleSave}
        onShareEvent={showSuccess}
        paymentUI={paymentUI}
        ticketLoading={paymentLoading}
      />
    </View>
  );
}

function rawToInitialValues(
  raw: Record<string, unknown>,
): Partial<EventFormValues> {
  const tier = (
    raw.event_ticket_kind as Record<string, unknown>[] | undefined
  )?.[0];
  return {
    currency: (tier?.currency as string) ?? "usd",
    description: (raw.description as string) ?? "",
    duration_minutes: (() => {
      if (raw.starts_at && raw.ends_at) {
        const start = DateTime.fromISO(raw.starts_at as string);
        const end = DateTime.fromISO(raw.ends_at as string);
        if (start.isValid && end.isValid) {
          return String(Math.round(end.diff(start, "minutes").minutes));
        }
      }
      return "";
    })(),
    flyer_url: (raw.flyer_url as string) ?? "",
    location_city: (raw.location_city as string) ?? "",
    location_country: (raw.location_country as string) ?? "US",
    location_lat: "",
    location_lng: "",
    location_name: (raw.location_name as string) ?? "",
    location_neighborhood: (raw.location_neighborhood as string) ?? "",
    location_place_id: (raw.location_place_id as string) ?? "",
    location_postal_code: (raw.location_postal_code as string) ?? "",
    location_state: (raw.location_state as string) ?? "",
    location_street: (raw.location_street as string) ?? "",
    price_cents: tier ? String(tier.price_cents ?? 0) : "",
    price_service_fee: tier ? String(tier.price_service_fee ?? 0) : "",
    quantity_total: tier ? String(tier.quantity_total ?? 10) : "",
    starts_at: (raw.starts_at as string) ?? "",
    status: (raw.status as string) ?? "draft",
    ticket_description: (tier?.description as string) ?? "",
    ticket_name: (tier?.name as string) ?? "",
    timezone: (raw.timezone as string) ?? "America/Los_Angeles",
    title: (raw.title as string) ?? "",
    visibility: (raw.visibility as string) ?? "public",
  };
}

const styles = StyleSheet.create({
  center: {
    alignItems: "center",
    justifyContent: "center",
  },
  container: {
    backgroundColor: colors.transparent,
    flex: 1,
  },
  errorText: {
    color: colors.light,
    fontSize: 18,
  },
  fill: {
    flex: 1,
  },
});
